import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, Paper, Typography, List, ListItem, ListItemIcon, ListItemText, Chip, IconButton, InputAdornment, Divider, useTheme, alpha, Backdrop, useMediaQuery } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon, Business as BusinessIcon, LocationOn as LocationIcon, AccountTree as SubsidiaryIcon, Receipt as TransactionIcon, Assignment as ProjectIcon, BusinessCenter as SupplierIcon, Inventory as CapitalGoodIcon, Rule as RuleIcon, DirectionsCar as VehicleIcon } from '@mui/icons-material';
import OrganisationService from '../../../shared/services/organisation/organisation.service';
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels';
import { LabelKey } from '../../../context/screen-labels/screen-labels.model';
import { UserDetailsContext } from '../../../context/user-details/user-details';

interface SearchResult {
  tableName: string;
  id: string;
  label: string;
}

interface SearchResponse {
  results: SearchResult[];
  totalResults: number;
  searchTerm: string;
}

const GlobalSearch: React.FC = () => {
  //Services
  const organisationService = useMemo(() => new OrganisationService(), []);
  //Context
  const userDetailsContext = useContext(UserDetailsContext);
  //State
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  //Ref
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isBelowTablet = useMediaQuery(theme.breakpoints.down('md'), { defaultMatches: false });
  const navigate = useNavigate();
  const { getLabel } = useContext(ScreenLabelsContext);

  /** Below md: start collapsed (icon only); md+: expanded. Updates on resize. */
  useLayoutEffect(() => {
    setIsExpanded(!isBelowTablet);
  }, [isBelowTablet]);

  const getTableIcon = (tableName: string) => {
    switch (tableName) {
      case 'vehicles':
        return <VehicleIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />;
      case 'locations':
        return <LocationIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />;
      case 'subsidiaries':
        return <SubsidiaryIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />;
      case 'transactions':
        return <TransactionIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />;
      case 'projects':
        return <ProjectIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />;
      case 'contactSuppliers':
        return <SupplierIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />;
      case 'capitalGoods':
        return <CapitalGoodIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />;
      case 'rules':
        return <RuleIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />;
      default:
        return <BusinessIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />;
    }
  };

  const getTableDisplayName = (tableName: string) => {
    const keyMap: Record<string, LabelKey> = {
      vehicles: 'vehicle',
      locations: 'location',
      subsidiaries: 'subsidiary',
      projects: 'project',
      contactSuppliers: 'supplier',
    };
    if (keyMap[tableName]) {
      return getLabel(keyMap[tableName], { plural: true, capitalize: 'first' });
    }
    switch (tableName) {
      case 'transactions':
        return 'Emissions';
      case 'capitalGoods':
        return 'Products';
      case 'rules':
        return 'Custom Rules';
      default:
        return tableName;
    }
  };

  const getRouteForTable = (tableName: string, id: string) => {
    switch (tableName) {
      case 'vehicles':
        return `/organisation/vehicles/${id}`;
      case 'locations':
        return `/organisation/locations/${id}`;
      case 'subsidiaries':
        return `/organisation/subsidiaries/${id}`;
      case 'transactions':
        return `/emissions/overview/${id}`;
      case 'projects':
        return `/organisation/projects/${id}`;
      case 'contactSuppliers':
        return `/organisation/suppliers/${id}`;
      case 'capitalGoods':
        return `/organisation/products/${id}`;
      case 'rules':
        return `/settings/data-upload/custom-rules`;
      default:
        return '/';
    }
  };

  const highlightSearchTerm = (text: string, term: string) => {
    if (!term) return text;
    const regex = new RegExp(`(${term})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: '#ffeb3b', fontWeight: 'bold' }}>
          {part}
        </span>
      ) : part
    );
  };

  const performSearch = async (term: string) => {
    if (term.length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response: SearchResponse = await organisationService.searchOrganisation(term);
      const enableSubsidiaries = userDetailsContext.userDetails?.organisation.enableSubsidiaries;
      const enableProjects = userDetailsContext.userDetails?.organisation.enableProjects;
      let filteredResults = response.results;
      if (!enableSubsidiaries) {
        filteredResults = filteredResults.filter(r => r.tableName !== 'subsidiaries');
      }
      if (!enableProjects) {
        filteredResults = filteredResults.filter(r => r.tableName !== 'projects');
      }
      setResults(filteredResults);
      setShowResults(true);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to perform search');
      setResults([]);
      setShowResults(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(value);
      }, 500);
    } else {
      setResults([]);
      setShowResults(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setResults([]);
    setShowResults(false);
    setError(null);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchInputRef.current?.focus();
  };

  const handleResultClick = (result: SearchResult) => {
    const route = getRouteForTable(result.tableName, result.id);
    navigate(route);
    setSearchTerm('');
    setResults([]);
    setShowResults(false);
    setError(null);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setSearchTerm('');
        setResults([]);
        setShowResults(false);
        setError(null);
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const groupedResults = useMemo(() => {
    const groups: { [key: string]: SearchResult[] } = {};
    results.forEach(result => {
      if (!groups[result.tableName]) {
        groups[result.tableName] = [];
      }
      groups[result.tableName].push(result);
    });
    return groups;
  }, [results]);

  return (
    <Box
      sx={{
        position: 'relative',
        mr: 1.5,
        flexShrink: isExpanded ? 1 : 0,
        flexGrow: isExpanded ? 1 : 0,
        minWidth: isExpanded ? { xs: 120, sm: 200 } : 'auto',
        maxWidth: isExpanded ? { xs: 'min(100%, 360px)', sm: 320, md: 320 } : 44,
      }}
    >
      <Backdrop
        open={showResults}
        onClick={() => {
          setSearchTerm('');
          setResults([]);
          setShowResults(false);
          setError(null);
          if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
          }
        }}
        sx={{ color: '#fff', zIndex: 1299, backgroundColor: 'rgba(0,0,0,0.3)' }}
      />
      {!isExpanded && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            aria-label="open search"
            onClick={() => {
              setIsExpanded(true);
              setTimeout(() => searchInputRef.current?.focus(), 0);
            }}
            edge="end"
            size="small"
            sx={{ p: 0.5 }}
          >
            <SearchIcon sx={{ color: theme.palette.text.secondary }} />
          </IconButton>
        </Box>
      )}
      {isExpanded && (
        <TextField
          ref={searchInputRef}
          fullWidth
          placeholder="Search..."
          value={searchTerm}
          onChange={handleInputChange}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton
                  aria-label="collapse search"
                  edge="start"
                  size="small"
                  onClick={() => {
                    setSearchTerm('');
                    setResults([]);
                    setShowResults(false);
                    setIsExpanded(false);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                  sx={{ p: 0.5 }}
                >
                  <ClearIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              '&:hover': {
                backgroundColor: theme.palette.background.paper,
              },
              '&.Mui-focused': {
                backgroundColor: theme.palette.background.paper,
              },
            },
          }}
        />
      )}
      {showResults && (
        <Paper
          ref={resultsRef}
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            left: { xs: '50%', md: '-160px' },
            transform: { xs: 'translateX(-50%)', md: 'none' },
            mt: 1,
            maxHeight: '70vh',
            overflow: 'auto',
            zIndex: 1300,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            width: { xs: 'min(calc(100vw - 24px), 720px)', md: '720px' },
            minWidth: { xs: 'min(100%, 320px)', md: '720px' },
            maxWidth: { xs: 'calc(100vw - 24px)', md: '720px' },
          }}
        >
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
              All search results
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {results.length} result{results.length !== 1 ? 's' : ''} for "{searchTerm}"
            </Typography>
          </Box>
          {isLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">Searching...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : results.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No results found</Typography>
            </Box>
          ) : (
            <List sx={{ p: 0, minWidth: 'max-content' }}>
              {Object.entries(groupedResults).map(([tableName, tableResults], groupIndex) => (
                <React.Fragment key={tableName}>
                  {groupIndex > 0 && <Divider />}
                  <Box sx={{ px: 2, py: 1, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                    <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                      {getTableDisplayName(tableName)}
                    </Typography>
                  </Box>
                  {tableResults.map((result, index) => (
                    <ListItem
                      key={`${result.tableName}-${result.id}`}
                      button
                      onClick={() => handleResultClick(result)}
                      sx={{
                        px: 2,
                        py: 1.5,
                        minWidth: 'max-content',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getTableIcon(result.tableName)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                            {highlightSearchTerm(result.label, searchTerm)}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'nowrap' }}>
                            <Chip
                              label={getTableDisplayName(result.tableName)}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                '& .MuiChip-label': { px: 1 }
                              }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default GlobalSearch;
